// paystackCheckout.ts
// Udemy-style instant checkout: opens the Paystack inline popup OVER the current page
// instead of doing a full-page redirect to Paystack's hosted page.
//
// How it works:
//   1. The backend /payment/initialize creates the transaction (server-side, secret key)
//      and returns an `accessCode` + a fallback `authorizationUrl`.
//   2. We load Paystack's official v2 inline script and call `resumeTransaction(accessCode)`.
//      resumeTransaction needs NO public key on the client — the access code is enough.
//   3. If the inline script can't load (poor connection / very old browser), we fall back
//      to the hosted-page redirect so checkout never dead-ends.

import { api } from '../services/api'
import type { PlanId } from './pendingPlan'

interface PaystackTransaction {
    reference: string
    status?: string
    message?: string
}

interface ResumeCallbacks {
    onSuccess?: (transaction: PaystackTransaction) => void
    onCancel?: () => void
    onError?: (error: unknown) => void
    onLoad?: (response: unknown) => void
}

interface PaystackPopInstance {
    resumeTransaction: (accessCode: string, callbacks?: ResumeCallbacks) => void
}

declare global {
    interface Window {
        PaystackPop?: new () => PaystackPopInstance
    }
}

const INLINE_SRC = 'https://js.paystack.co/v2/inline.js'
let inlineLoader: Promise<boolean> | null = null

/** Loads Paystack inline v2 once. Resolves false if it can't load (so callers can fall back). */
function loadInlineScript(): Promise<boolean> {
    if (typeof window === 'undefined') return Promise.resolve(false)
    if (window.PaystackPop) return Promise.resolve(true)
    if (inlineLoader) return inlineLoader

    inlineLoader = new Promise<boolean>(resolve => {
        const finish = () => resolve(Boolean(window.PaystackPop))
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${INLINE_SRC}"]`)
        if (existing) {
            if (window.PaystackPop) return finish()
            existing.addEventListener('load', finish)
            existing.addEventListener('error', () => resolve(false))
            return
        }
        const script = document.createElement('script')
        script.src = INLINE_SRC
        script.async = true
        script.onload = finish
        script.onerror = () => {
            inlineLoader = null // allow a future retry
            resolve(false)
        }
        document.head.appendChild(script)
    })
    return inlineLoader
}

export interface InlineCheckoutHandlers {
    /** Called with the Paystack reference once the popup reports success. */
    onSuccess: (reference: string) => void
    /** Called when the user closes the popup without paying. */
    onCancel?: () => void
    /** Called if initialization or the popup itself errors (the redirect fallback is tried first). */
    onError?: (error: unknown) => void
}

/**
 * Initializes a transaction and opens the Paystack inline popup.
 * Throws if `/payment/initialize` fails — callers should wrap in try/catch to show an error.
 * Falls back to a hosted-page redirect when the inline script is unavailable.
 * An optional signal prevents a late popup/redirect after its owning screen closes.
 * It does not reverse a server transaction that has already been initialized.
 */
export async function openInlineCheckout(
    plan: PlanId,
    handlers: InlineCheckoutHandlers,
    signal?: AbortSignal,
): Promise<void> {
    if (signal?.aborted) return
    const { accessCode, authorizationUrl } = await api.initializePayment(plan)
    if (signal?.aborted) return

    const ready = await loadInlineScript()
    if (signal?.aborted) return
    if (ready && window.PaystackPop && accessCode) {
        const popup = new window.PaystackPop()
        popup.resumeTransaction(accessCode, {
            onSuccess: transaction => { if (!signal?.aborted) handlers.onSuccess(transaction.reference) },
            onCancel: () => { if (!signal?.aborted) handlers.onCancel?.() },
            onError: error => { if (!signal?.aborted) handlers.onError?.(error) },
        })
        return
    }

    // Inline unavailable — never dead-end: redirect to Paystack's hosted checkout.
    window.location.assign(authorizationUrl)
}
